import { AxiosResponse } from "axios";
import { API_ROOT } from "services/baseURLs.config";
import { axiosRequest } from "../api.utils";

const fetchCompanyStructure = async (
  companyId: number
): Promise<AxiosResponse> => {
  return await axiosRequest.auth.get<any>(
    `${API_ROOT}/companies/${companyId}/structure/v2`
  );
};

const fetchTeamMembers = async (
  companyId: number,
  workGroupId: number,
  page: number,
  pageSize: number
): Promise<AxiosResponse> => {
  return await axiosRequest.auth.get<any>(
    `${API_ROOT}/companies/${companyId}/workers/v2?page=${page}&pageSize=${pageSize}&workgroupId=${workGroupId}&workGroupFilter=InWorkGroup`
  );
};

const fetchCompanyWorkers = async (
  companyId: number,
  page: number,
  pageSize: number,
  active: boolean,
  filter: string
): Promise<AxiosResponse> => {
  return await axiosRequest.auth.get<any>(
    `${API_ROOT}/companies/${companyId}/workers/v2?page=${page}&pageSize=${pageSize}&workGroupFilter=${filter}&activeOnly=${active}`
  );
};

const fetchUnassignedWorkers = async (
  companyId: number,
  page: number,
  pageSize: number
): Promise<AxiosResponse> => {
  return await axiosRequest.auth.get<any>(
    `${API_ROOT}/companies/${companyId}/workers/v2?page=${page}&pageSize=${pageSize}&workGroupFilter=WithoutWorkGroup`
  );
};

const fetchWorkerProfile = async (
  companyId: number,
  workerId: number
): Promise<AxiosResponse> => {
  return await axiosRequest.auth.get<any>(
    `${API_ROOT}/companies/${companyId}/workers/${workerId}/v2?includeDocuments=true`
  );
};

const assignWorkerToTeam = async (
  companyId: number,
  workGroupId: number,
  workerId: number
): Promise<AxiosResponse> => {
  return await axiosRequest.auth.post<any>(
    `${API_ROOT}/companies/${companyId}/workgroups/${workGroupId}/workers/${workerId}/v2`
  );
};

const createTeam = async (
  companyId: number,
  name: string,
  companyUnitId: number
): Promise<AxiosResponse> => {
  return await axiosRequest.auth.post<any>(
    `${API_ROOT}/companies/${companyId}/workgroups`,
    {
      companyId,
      name,
      companyUnitId,
    }
  );
};

const createEntity = async (
  companyId: number,
  name: string
): Promise<AxiosResponse> => {
  return await axiosRequest.auth.post<any>(
    `${API_ROOT}/companies/${companyId}/structure/entities`,
    { name }
  );
};

const createUnit = async (
  companyId: number,
  entityId: number,
  name: string
): Promise<AxiosResponse> => {
  return await axiosRequest.auth.post<any>(
    `${API_ROOT}/companies/${companyId}/structure/entities/${entityId}/units`,
    { name }
  );
};

const deleteUnit = async (
  companyId: number,
  entityId: number,
  unitId: number
): Promise<AxiosResponse> => {
  return await axiosRequest.auth.delete<any>(
    `${API_ROOT}/companies/${companyId}/structure/entities/${entityId}/units/${unitId}`
  );
};

const deleteEntity = async (
  companyId: number,
  entityId: number
): Promise<AxiosResponse> => {
  return await axiosRequest.auth.delete<any>(
    `${API_ROOT}/companies/${companyId}/structure/entities/${entityId}`
  );
};

const deleteTeam = async (
  companyId: number,
  workGroupId: number
): Promise<AxiosResponse> => {
  return await axiosRequest.auth.delete<any>(
    `${API_ROOT}/companies/${companyId}/workgroups/${workGroupId}`
  );
};

const updateTeam = async (
  companyId: number,
  workGroupId: number,
  name: string,
  companyUnitId?: number
): Promise<AxiosResponse> => {
  return await axiosRequest.auth.put<any>(
    `${API_ROOT}/companies/${companyId}/workgroups/${workGroupId}`,
    { name, companyUnitId }
  );
};

const updateUnit = async (
  companyId: number,
  entityId: number,
  unitId: number,
  name: string
): Promise<AxiosResponse> => {
  return await axiosRequest.auth.put<any>(
    `${API_ROOT}/companies/${companyId}/structure/entities/${entityId}/units/${unitId}`,
    { name }
  );
};

const updateEntity = async (
  companyId: number,
  entityId: number,
  name: string
): Promise<AxiosResponse> => {
  return await axiosRequest.auth.put<any>(
    `${API_ROOT}/companies/${companyId}/structure/entities/${entityId}`,
    { name }
  );
};

const createWorker = async (
  companyId: number,
  data: any
): Promise<AxiosResponse> => {
  return await axiosRequest.auth.post<any>(
    `${API_ROOT}/companies/${companyId}/workers/v2`,
    data
  );
};

const updateWorker = async (
  companyId: number,
  workerId: number,
  data: any
): Promise<AxiosResponse> => {
  return await axiosRequest.auth.put<any>(
    `${API_ROOT}/companies/${companyId}/workers/${workerId}/v2`,
    data
  );
};

const deleteWorker = async (
  companyId: number,
  workerId: number
): Promise<AxiosResponse> => {
  return await axiosRequest.auth.delete<any>(
    `${API_ROOT}/companies/${companyId}/workers/${workerId}/v2`
  );
};

const unassignWorkerFromWorkGroup = async (
  companyId: number,
  workGroupId: number,
  workerId: number
): Promise<AxiosResponse> => {
  return await axiosRequest.auth.delete<any>(
    `${API_ROOT}/companies/${companyId}/workgroups/${workGroupId}/workers/${workerId}/v2`
  );
};

const getWorkGroupSettings = async (
  companyId: number,
  workGroupId: number
): Promise<AxiosResponse> => {
  return await axiosRequest.auth.get<any>(
    `${API_ROOT}/companies/${companyId}/workgroups/${workGroupId}/settings`
  );
};

const updateTeamSetting = async (
  companyId: number,
  workGroupId: number,
  key: string,
  value: string | null
): Promise<AxiosResponse<any>> => {
  return await axiosRequest.auth.put(
    `${API_ROOT}/companies/${companyId}/workgroups/${workGroupId}/settings`,
    {
      key,
      value,
    }
  );
};

const getWorkerByNumber = async (
  companyId: number,
  phoneNumber: string
): Promise<AxiosResponse> => {
  return await axiosRequest.auth.get<any>(
    `${API_ROOT}/companies/${companyId}/workers/v2/phoneNumber/${phoneNumber}`
  );
};

const getEmployeeNumber = async (
  companyId: number
): Promise<AxiosResponse> => {
  return await axiosRequest.auth.get<any>(
    `${API_ROOT}/companies/${companyId}/workers/employeeNumber`
  );
};

export const organizationalStructure = {
  fetchCompanyStructure,
  fetchTeamMembers,
  fetchWorkerProfile,
  fetchCompanyWorkers,
  assignWorkerToTeam,
  createTeam,
  createUnit,
  createEntity,
  deleteUnit,
  deleteEntity,
  deleteTeam,
  updateUnit,
  updateEntity,
  updateTeam,
  createWorker,
  updateWorker,
  deleteWorker,
  fetchUnassignedWorkers,
  unassignWorkerFromWorkGroup,
  getWorkGroupSettings,
  updateTeamSetting,
  getWorkerByNumber,
  getEmployeeNumber
};
